import { useCallback, useMemo } from "react";
import { useParams } from "next/navigation";
import { pubsub } from "utils";
import { useSnapshot } from "valtio";
import type {
  PortfolioContentMode,
  PortfolioItem,
} from "../components/PortfolioContainer/types";

import { EditPortfolioModalCreateAble } from "../components/EditPortfolioModal";
import { getAllPortfolioOption } from "../components/PortfolioContainer/constants";
import { pubSubKeyMap } from "../utils/pubSubKeyMap";
import { useMemberUser } from "./useMemberUser";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/hooks/usePortfolioContent";
import { favoritesCenter } from "@/src/store/favorite/FavoritesCenter";
import { openEmailClient } from "@/src/utils";
import { usePathname } from "@hooks/usePathname";
import { useShellRouter } from "./useShellAwareRouter";

const I18n = I18nUtil.createScoped(Translations);

/**
 * Portfolio 内容业务逻辑 Hook
 *
 * 封装了 PortfolioContent 的所有业务逻辑:
 * - 数据获取和映射
 * - 会员等级计算
 * - 投资组合限制逻辑
 * - 按钮显示控制
 * - 创建投资组合处理
 *
 * @param contentId - 内容ID，用于获取收藏状态（checkbox模式时需要）
 */
export const usePortfolioContent = (
  contentId?: string,
  mode?: PortfolioContentMode,
) => {
  const router = useShellRouter();
  const pathName = usePathname();
  // 路由参数（用于 select 模式勾选当前 portfolio）
  const params = useParams?.() as { portfolioId?: string } | null;
  const selectedPortfolioId = params?.portfolioId as string | undefined;
  // 获取会员信息
  const { data: memberInfo } = useMemberUser();
  const ALL_PORTFOLIO_OPTION = getAllPortfolioOption();

  // 获取收藏夹数据
  const {
    userFolders,
    isLoading,
    favoriteStates: favoriteStatesMap,
  } = useSnapshot(favoritesCenter.state);

  // 将 FolderInfo 映射为 PortfolioItem
  const portfolioData = useMemo<PortfolioItem[]>(() => {
    if (!userFolders) {
      return [];
    }
    // 从 favoriteStatesMap 获取当前 contentId 的收藏状态
    const currentFavoriteStates = contentId
      ? favoriteStatesMap.data[contentId]
      : undefined;

    const baseOptions = userFolders.map((folder) => ({
      id: folder.id,
      label: folder.name,
      isChecked:
        mode === "select"
          ? folder.id === selectedPortfolioId
          : (currentFavoriteStates?.includes(folder.id) ?? false),
      isLocked: folder.status !== "NORMAL",
      isFull: folder.asset_count >= 30,
    }));

    // 在选择模式下，增加一个 "All" 选项置于首位
    if (mode === "select") {
      return [
        {
          id: ALL_PORTFOLIO_OPTION.id,
          label: ALL_PORTFOLIO_OPTION.label,
          isChecked:
            !selectedPortfolioId ||
            selectedPortfolioId === ALL_PORTFOLIO_OPTION.id,
          isLocked: false,
          isFull: false,
        },
        ...baseOptions,
      ];
    }

    return baseOptions;
  }, [
    userFolders,
    favoriteStatesMap.data,
    contentId,
    mode,
    selectedPortfolioId,
  ]);

  // 过滤出可用的投资组合
  const filterAvailablePortfolios = useMemo(() => {
    return userFolders?.filter((folder) => folder.status === "NORMAL");
  }, [userFolders]);

  // 计算会员等级
  const memberLevel = useMemo(() => {
    if (memberInfo?.isExpert) {
      return "export" as const;
    }
    if (memberInfo?.isPro) {
      return "pro" as const;
    }
    return "free" as const;
  }, [memberInfo?.isExpert, memberInfo?.isPro]);

  // 根据会员等级计算投资组合限制
  const portfolioLimit = useMemo(() => {
    switch (memberLevel) {
      case "export":
        return 20;
      case "pro":
        return 10;
      case "free":
      default:
        return 2;
    }
  }, [memberLevel]);

  // 当前投资组合数量
  const currentCount = portfolioData.filter(
    (i) => !i.isLocked && i.id !== ALL_PORTFOLIO_OPTION.id,
  ).length;

  // 是否达到限制
  const hasReachedLimit = currentCount >= portfolioLimit;

  // 是否有锁定的投资组合
  const hasLockedPortfolios = portfolioData.some((item) => item.isLocked);

  // Expert 用户达到限制的特殊情况
  const isExpertAtLimit = memberInfo?.isExpert && hasReachedLimit;

  // 是否显示通知（达到限制 或 有锁定的投资组合）
  const shouldShowNotification = hasLockedPortfolios;

  // 通知消息（根据不同情况显示不同文案）
  const notificationMessage = I18n.t("Unlock Remaining Portfolios");

  // 是否显示升级按钮（已达到限制且非 Expert 或 有锁定的投资组合）
  const shouldShowUpgradeButton = hasReachedLimit && !memberInfo?.isExpert;

  const shouldShowCreateButton = useMemo(() => {
    if (
      (!hasReachedLimit || isExpertAtLimit) &&
      !pathName.includes("/portfolio/market")
    ) {
      return true;
    }
    if (!hasReachedLimit && pathName.includes("/portfolio/market")) {
      return true;
    }
  }, [hasReachedLimit, isExpertAtLimit, pathName]);

  // 创建按钮类型（Expert 达到限制时显示为 manage）
  const createButtonType: "manage" | "create" = isExpertAtLimit
    ? "manage"
    : "create";

  // 处理创建投资组合
  const handleCreatePortfolio = useCallback(async () => {
    if (isExpertAtLimit) {
      pubsub.emit(pubSubKeyMap.CLOSE_SEARCH_MODAL);
      router.push("/portfolio/market/all");
      return;
    }
    await EditPortfolioModalCreateAble.call({
      initialValue: `Portfolio ${currentCount + 1}`,
    });
  }, [currentCount, isExpertAtLimit, router]);

  // 处理升级点击（与 UpgradeButton 逻辑相同）
  const handleUpgradeClick = useCallback(() => {
    pubsub.emit(pubSubKeyMap.CLOSE_SEARCH_MODAL);
    if (memberLevel === "free") {
      router.push("/member");
      return;
    }
    if (memberLevel === "pro") {
      openEmailClient();
      return;
    }
  }, [memberLevel, router]);

  return {
    // 数据
    portfolioData,
    isLoading,
    memberLevel,

    // 限制信息
    portfolioLimit,
    currentCount,
    hasReachedLimit,
    hasLockedPortfolios,
    isExpertAtLimit,

    // UI 控制
    shouldShowNotification,
    notificationMessage,
    shouldShowUpgradeButton,
    shouldShowCreateButton,
    createButtonType,

    // 事件处理
    handleCreatePortfolio,
    handleUpgradeClick,
    filterAvailablePortfolios,
  };
};
